/** @jsx jsx */
import { jsx } from '@emotion/core'
import { FunctionComponent, useMemo, Fragment, useState, ReactElement } from 'react'
import { RouteComponentProps } from '@reach/router'
import { useObserver } from 'mobx-react-lite'
import { ChevronDown, ChevronUp } from 'react-feather'

import {
  Screen,
  InputWithLabel,
  SelectWithLabel,
  CheckboxGroup,
  Footer,
  Message,
  Contacts,
} from '~/components'
import {
  PartGroups,
  PartOrigins,
  PartConditions,
  EPartOrigins,
  EPartConditions,
} from '~/types'
import { UserStore, ApiStore } from '~/stores'
import { NewRequestStore } from './store'
import { styles } from './styles'

interface NewRequestScreenProps extends RouteComponentProps {
  userStore: UserStore
  apiStore: ApiStore
}

export const NewRequestScreen: FunctionComponent<NewRequestScreenProps> = ({
  location,
  navigate,
  userStore,
  apiStore,
}) => {
  const store = useMemo(() => new NewRequestStore(userStore, apiStore), [])

  const contactsVisible = Boolean(
    store.request.Address1 && store.request.City && store.request.State,
  )

  return useObserver(() => (
    <Screen
      id="new-request"
      title="New Request"
      description="Create a new part request on PartScout.me"
      url={location.href}
    >
      <div css={styles.container}>
        <form
          css={styles.content}
          onSubmit={(e) => {
            e.preventDefault()
            store
              .createRequest()
              .then(() => navigate('/requests'))
              .catch(console.error)
          }}
        >
          <Section title="PART INFO" padded>
            <div css={[styles.section.side, styles.section.sidePadded]}>
              <InputWithLabel
                label="name"
                id="part-name"
                name="part-name"
                placeholder="Brake pads"
                type="text"
                autoComplete="off"
                required
                disabled={store.loading}
                tabIndex={7}
                value={store.request.Name}
                onChange={(e) => store.setRequest({ Name: e.target.value })}
              />
              <SelectWithLabel
                label="group"
                id="part-group"
                name="part-group"
                placeholder="Engine, Body, Suspension, etc."
                autoComplete="off"
                required
                disabled={store.loading}
                tabIndex={8}
                options={PartGroups.map(([name, value]) => ({ name, value }))}
                selected={store.request.Group || ''}
                onChange={(v: any) => store.setRequest({ Group: Number(v) })}
              />
            </div>

            <div css={[styles.section.side, styles.section.sidePadded]}>
              <CheckboxGroup
                label="Origin"
                options={PartOrigins.map(([name, value]) => ({ name, value }))}
                selected={store.request.Origin}
                onChange={(v: EPartOrigins) => store.setRequest({ Origin: Number(v) })}
                required
                disabled={store.loading}
                tabIndex={9}
              />

              <CheckboxGroup
                label="Condition"
                options={PartConditions.map(([name, value]) => ({ name, value }))}
                selected={store.request.Condition}
                onChange={(v: EPartConditions) =>
                  store.setRequest({ Condition: Number(v) })
                }
                required
                disabled={store.loading}
                tabIndex={12}
              />
            </div>
          </Section>

          <Section title="CAR INFO">
            <div css={styles.section.side}>
              <InputWithLabel
                label="make"
                id="car-make"
                name="car-make"
                placeholder="Ford"
                type="text"
                autoComplete="off"
                required
                disabled={store.loading}
                tabIndex={15}
                value={store.request.CarMake}
                onChange={(e) => store.setRequest({ CarMake: e.target.value })}
              />

              <InputWithLabel
                label="engine"
                id="car-engine"
                name="car-engine"
                placeholder="5.0L V8"
                type="text"
                autoComplete="off"
                tabIndex={18}
                disabled={store.loading}
                value={store.request.CarEngine}
                onChange={(e) => store.setRequest({ CarEngine: e.target.value })}
              />
            </div>

            <div css={styles.section.side}>
              <InputWithLabel
                label="model"
                id="car-model"
                name="car-model"
                placeholder="F-150"
                type="text"
                autoComplete="off"
                required
                tabIndex={16}
                disabled={store.loading}
                value={store.request.CarModel}
                onChange={(e) => store.setRequest({ CarModel: e.target.value })}
              />

              <InputWithLabel
                label="vin"
                id="car-vin"
                name="car-vin"
                placeholder="4M2EU38806UJ10953"
                type="text"
                autoComplete="off"
                tabIndex={19}
                disabled={store.loading}
                value={store.request.CarVIN}
                onChange={(e) => store.setRequest({ CarVIN: e.target.value })}
              />
            </div>

            <div css={styles.section.side}>
              <InputWithLabel
                label="year"
                id="car-year"
                name="car-year"
                placeholder="2015"
                type="text"
                autoComplete="off"
                required
                tabIndex={17}
                disabled={store.loading}
                value={store.request.CarYear}
                onChange={(e) =>
                  store.setRequest({ CarYear: Number(e.target.value) || undefined })
                }
              />

              <InputWithLabel
                label="trim"
                id="car-trim"
                name="car-trim"
                placeholder="King Ranch"
                type="text"
                autoComplete="off"
                tabIndex={20}
                disabled={store.loading}
                value={store.request.CarTrim}
                onChange={(e) => store.setRequest({ CarTrim: e.target.value })}
              />
            </div>
          </Section>

          <Section
            title="CONTACTS"
            folded={contactsVisible}
            renderFooter={() => (
              <Message type="info">
                Only your <b>city</b> and <b>state</b> will be visible to other people.
                Once you accept an offer, the rest of your contacts will be shared with
                the offerer to contact you about your request.
              </Message>
            )}
          >
            <Contacts
              disabled={store.loading}
              contacts={store.request}
              setContacts={store.setRequest}
              required={true}
            />
          </Section>

          <button type="submit" css={styles.button} disabled={store.loading}>
            CREATE REQUEST
          </button>
        </form>

        <Footer />
      </div>
    </Screen>
  ))
}

interface SectionProps {
  title: string
  padded?: boolean
  folded?: boolean
  renderFooter?: () => ReactElement
}

export const Section: FunctionComponent<SectionProps> = ({
  title,
  padded,
  children,
  folded,
  renderFooter,
}) => {
  const [expanded, setExpanded] = useState(folded ? false : true)

  return useMemo(
    () => (
      <div css={[styles.section.wrapper, expanded && styles.section.expanded]}>
        <div css={styles.header.container} onClick={() => setExpanded(!expanded)}>
          <div css={styles.header.icon}></div>

          <div css={styles.header.title}>{title}</div>

          <div css={styles.header.icon}>
            {expanded ? (
              <ChevronDown size={18} color="white" />
            ) : (
              <ChevronUp size={18} color="white" />
            )}
          </div>
        </div>

        {expanded && (
          <section
            css={[styles.section.container, padded && styles.section.containerPadded]}
          >
            {children}
          </section>
        )}

        {expanded && renderFooter?.()}
      </div>
    ),
    [padded, expanded, children],
  )
}
